import { forwardRef, useMemo } from "react";

import { compactNumber } from "@/utils/number";

import {
  CartesianGrid,
  Line,
  LineChart as LineChartComponent,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

import ChartLegend from "./ChartLegend";
import { ChartTooltipContent } from "./GroupedChartTooltip";
import {
  buildSeriesFromData,
  ChartSeries,
  domainTickCalculate,
  measureTextWidth,
  resolveCategoryKey,
  shouldRotateAxisLabels
} from "./utils";

// Horizontal chart margins — not shared with the pinned Y-axis strip, which
// draws its ticks edge to edge.
const CHART_MARGIN_X = { left: 10, right: 20 };
// Smallest X-axis padding, used when the labels need no more room.
const AXIS_LABEL_BASE_PADDING = 20;
// Diagonal labels are anchored at their end and run up at -45°, so they reach
// cos(45°) of their width to the left, plus the tick's `dx` nudge.
const DIAGONAL_LABEL_COS = Math.cos(Math.PI / 4);
const DIAGONAL_LABEL_DX = 4;
// Width reserved for the pinned Y-axis strip beside the scroll area.
const Y_AXIS_STRIP_WIDTH = 60;
// Room each point needs before its label collides with its neighbour.
const POINT_WIDTH = 50;
// Chart margins and X-axis height, shared by the plot and the pinned Y-axis
// strip so their tick rows line up.
const CHART_MARGIN_Y = { top: 20, bottom: 30 };
const X_AXIS_HEIGHT = 70;

interface LineChartProps {
  data: Record<string, string | number>[];
  /** Key used as X-axis category label. Auto-detected if omitted. */
  categoryKey?: string;
  /** One entry per line. Auto-built from data keys if omitted. */
  series?: ChartSeries[];
  height?: number;
  title?: string;
  /** Aggregation appended to the legend label when the chart is single-series. */
  aggregation?: string;
}

const truncateLabel = (value: string, maxLength = 12) =>
  value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;

interface LineChartItemProps {
  width?: number;
  height?: number;
  data: Record<string, string | number>[];
  categoryKey: string;
  series: ChartSeries[];
  axisDomainMax: number;
  axisTicks: number[];
  isGrouped: boolean;
  totalValue: number;
}

const LineChartItem = ({
  width,
  height,
  data,
  categoryKey,
  series,
  axisDomainMax,
  axisTicks,
  isGrouped,
  totalValue
}: LineChartItemProps) => {
  const labels = data.map((item) => String(item[categoryKey] ?? ""));
  const widestLabel = labels.length
    ? Math.max(...labels.map((label) => measureTextWidth(label)))
    : 0;

  // The scroll container clips whatever spills past the plot edges. A straight
  // label is centred on its tick, so half of it hangs over each side.
  const straightPadding = Math.max(
    AXIS_LABEL_BASE_PADDING,
    Math.ceil(widestLabel / 2)
  );

  // Default to diagonal until `width` is known, to avoid a straight→diagonal
  // flash.
  const isDiagonalLabels =
    !width ||
    shouldRotateAxisLabels(
      labels,
      width - CHART_MARGIN_X.left - CHART_MARGIN_X.right - straightPadding * 2
    );

  // A diagonal label overhangs the left side only, and truncation caps how far.
  const widestDiagonalLabel = Math.max(
    0,
    ...labels.map((label) => measureTextWidth(truncateLabel(label)))
  );
  const diagonalPadding = Math.max(
    AXIS_LABEL_BASE_PADDING,
    Math.ceil(widestDiagonalLabel * DIAGONAL_LABEL_COS + DIAGONAL_LABEL_DX)
  );

  const xAxisPadding = isDiagonalLabels
    ? { left: diagonalPadding, right: AXIS_LABEL_BASE_PADDING }
    : { left: straightPadding, right: straightPadding };

  return (
    <LineChartComponent
      width={width}
      height={height}
      className="overflow-visible"
      data={data}
      margin={{ ...CHART_MARGIN_Y, ...CHART_MARGIN_X }}
    >
      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
      <XAxis
        dataKey={categoryKey}
        axisLine={false}
        tickLine={false}
        angle={isDiagonalLabels ? -45 : 0}
        textAnchor={isDiagonalLabels ? "end" : "middle"}
        dx={isDiagonalLabels ? -4 : 0}
        tick={{ fill: "#666", fontSize: 12 }}
        tickFormatter={(v) =>
          isDiagonalLabels ? truncateLabel(String(v)) : String(v)
        }
        interval={0}
        height={X_AXIS_HEIGHT}
        padding={xAxisPadding}
      />
      {/* Hidden — the pinned strip draws the ticks. Declared so the lines share
          that strip's domain and tick positions. */}
      <YAxis domain={[0, axisDomainMax]} ticks={axisTicks} hide />
      <Tooltip
        content={
          <ChartTooltipContent
            isGrouped={isGrouped}
            totalValue={totalValue}
            iconType="line"
          />
        }
      />
      {series.map((s) => (
        <Line
          key={s.dataKey}
          type="monotone"
          dataKey={s.dataKey}
          name={s.label ?? s.dataKey}
          stroke={s.color}
          strokeWidth={2}
          dot={{ r: 4, fill: s.color, stroke: s.color }}
          activeDot={{
            r: 6,
            fill: s.color,
            stroke: "#ffffff",
            strokeWidth: 2
          }}
        />
      ))}
    </LineChartComponent>
  );
};

const LineChart = forwardRef<HTMLDivElement, LineChartProps>((props, ref) => {
  const {
    data,
    categoryKey: categoryKeyProp,
    series: seriesProp,
    height = 400,
    title,
    aggregation
  } = props;

  const categoryKey = useMemo(
    () => resolveCategoryKey(data, categoryKeyProp),
    [categoryKeyProp, data]
  );

  const series = useMemo(
    () => seriesProp ?? buildSeriesFromData(data, categoryKey),
    [seriesProp, data, categoryKey]
  );

  const isGrouped = series.length > 1;

  const numericValues = useMemo(
    () =>
      data
        .flatMap((item) => series.map((s) => Number(item[s.dataKey])))
        .filter((v) => !Number.isNaN(v)),
    [data, series]
  );

  const totalValue = numericValues.reduce((sum, v) => sum + v, 0);

  const maxValue = numericValues.length > 0 ? Math.max(...numericValues, 0) : 0;

  const { axisDomainMax, axisTicks } = useMemo(
    () => domainTickCalculate(numericValues, maxValue),
    [numericValues, maxValue]
  );

  // The plot keeps its natural width and scrolls rather than squeezing every
  // label in. Short series stay under the container width, so `w-full` wins.
  const plotWidth = data.length * POINT_WIDTH;

  return (
    <div ref={ref} className="w-full flex flex-col" aria-label={title}>
      <div className="flex">
        {/* Pinned Y-axis — an axis-only chart sharing the plot's vertical
            margins and X-axis height so their tick rows line up. */}
        <div className="shrink-0" style={{ width: Y_AXIS_STRIP_WIDTH }}>
          <LineChartComponent
            width={Y_AXIS_STRIP_WIDTH}
            height={height}
            data={data}
            margin={{ ...CHART_MARGIN_Y, right: 0, left: 0 }}
          >
            <YAxis
              domain={[0, axisDomainMax]}
              ticks={axisTicks}
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#666", fontSize: 12 }}
              tickFormatter={(v) => compactNumber(v)}
              width={Y_AXIS_STRIP_WIDTH}
            />
            <XAxis
              dataKey={categoryKey}
              height={X_AXIS_HEIGHT}
              tick={false}
              tickLine={false}
              axisLine={false}
            />
          </LineChartComponent>
        </div>
        {/* Scrolling plot area. min-width keeps the plot's natural width;
            w-full lets it fill the parent when there's room. */}
        <div className="flex-1 overflow-x-auto">
          <div className="w-full" style={{ minWidth: plotWidth }}>
            <ResponsiveContainer width="100%" height={height}>
              <LineChartItem
                data={data}
                categoryKey={categoryKey}
                series={series}
                axisDomainMax={axisDomainMax}
                axisTicks={axisTicks}
                isGrouped={isGrouped}
                totalValue={totalValue}
              />
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <ChartLegend series={series} iconType="line" aggregation={aggregation} />
    </div>
  );
});

LineChart.displayName = "LineChart";

export default LineChart;
